import { ROUTES } from "../constants";
import { isRsplFeatureEnabled } from "./rspl";

const ANALYTICS_MODULE_KEY = "ITEMS_TEXT.ANALYTICS";

const safeHasPermission = (hasPermission, key) => {
	if (typeof hasPermission !== "function" || !key) return false;
	try {
		return !!hasPermission(key);
	} catch {
		return false;
	}
};

const settingsPermissionKeys = [
	"app_settings.manage_settings",
	"app_settings.manage_order_settings",
	"app_settings.manage_inventory_settings",
	"app_settings.manage_site_settings",
	"app_settings.manage_reminder_settings",
	"app_settings.manage_cart_retention",
];

const isModuleEnabled = (enabledAdminModuleSet, key) => {
	if (!enabledAdminModuleSet || typeof enabledAdminModuleSet.has !== "function") return true;
	return enabledAdminModuleSet.has(key);
};

const isSuperAdminUser = (user) => Number(user?.user_id) === 1;

const isAdminRoleUser = (user) => {
	const roleName = String(user?.role?.role_name || user?.user_role_name || user?.user_role || "").trim().toLowerCase();
	const roleType = String(user?.role?.role_type || "").trim().toLowerCase();
	return Number(user?.user_id) === 1 || roleName === "admin" || roleType === "admin";
};

const isDelkorAdminUser = (user) => {
	if (isSuperAdminUser(user)) return true;
	if (!isAdminRoleUser(user)) return false;

	const roleId = Number(user?.role?.role_id ?? user?.role_id ?? user?.user_role);
	const roleName = String(user?.role?.role_name || user?.user_role_name || user?.user_role || "").trim().toLowerCase();

	return roleId === 1 || roleName === "delkor admin";
};

const canUseAnalyticsModule = (enabledAdminModuleSet) =>
	isModuleEnabled(enabledAdminModuleSet, ANALYTICS_MODULE_KEY);

export const canAccessAnalytics = (hasPermission, enabledAdminModuleSet, user) => {
	if (!canUseAnalyticsModule(enabledAdminModuleSet)) return false;
	return (
		isSuperAdminUser(user) ||
		(
			isDelkorAdminUser(user) &&
			(
				safeHasPermission(hasPermission, "analytics") ||
				safeHasPermission(hasPermission, "analytics.view_user_analytics") ||
				safeHasPermission(hasPermission, "analytics.view_order_analytics")
			)
		)
	);
};

export const canAccessServices = (hasPermission) =>
	safeHasPermission(hasPermission, "service_management.view_cases") ||
	safeHasPermission(hasPermission, "service_management.view_workorders");

export const canAccessOrderHistory = (hasPermission) =>
	safeHasPermission(hasPermission, "order_management.view_order_history") ||
	safeHasPermission(hasPermission, "order_management.create_order_schedules");

export const canAccessSettings = (user) => user?.user_id === 1;

export const canAccessUserAnalytics = (hasPermission, user, enabledAdminModuleSet) =>
	canUseAnalyticsModule(enabledAdminModuleSet) &&
	(
		Number(user?.user_id) === 1 ||
		(
			isDelkorAdminUser(user) &&
			(
				safeHasPermission(hasPermission, "analytics.view_user_analytics") ||
				safeHasPermission(hasPermission, "analytics")
			)
		)
	);

export const canAccessOrderAnalytics = (hasPermission, user, enabledAdminModuleSet) =>
	canUseAnalyticsModule(enabledAdminModuleSet) &&
	(
		Number(user?.user_id) === 1 ||
		(
			isDelkorAdminUser(user) &&
			(
				safeHasPermission(hasPermission, "analytics.view_order_analytics") ||
				safeHasPermission(hasPermission, "analytics")
			)
		)
	);
export const canAccessAdmin = (hasPermission, user) => {
	const isSuperAdmin = user?.user_id === 1;
	const checks = [
		() => safeHasPermission(hasPermission, "user_management.create_users") || safeHasPermission(hasPermission, "user_management.edit_users") || safeHasPermission(hasPermission, "user_management.delete_users"),
		() => safeHasPermission(hasPermission, "role_management.create_roles") || safeHasPermission(hasPermission, "role_management.edit_roles") || safeHasPermission(hasPermission, "role_management.delete_roles"),
		() => safeHasPermission(hasPermission, "site_management.edit_sites"),
		() => safeHasPermission(hasPermission, "document_management.create_documents") || safeHasPermission(hasPermission, "document_management.edit_documents") || safeHasPermission(hasPermission, "document_management.delete_documents"),
		() => safeHasPermission(hasPermission, "machine_provision_management.provision_machines") || safeHasPermission(hasPermission, "machine_provision_management.edit_provisions") || safeHasPermission(hasPermission, "machine_provision_management.delete_provisions"),
		() => safeHasPermission(hasPermission, "app_settings.allow_model_uploads") || safeHasPermission(hasPermission, "app_settings.manage_change_parts"),
		() => safeHasPermission(hasPermission, "order_management.create_order_schedules") || safeHasPermission(hasPermission, "order_management.edit_order_schedules") || safeHasPermission(hasPermission, "order_management.delete_order_schedules"),
		() => settingsPermissionKeys.some((perm) => safeHasPermission(hasPermission, perm)),
		() => safeHasPermission(hasPermission, "app_settings.manage_price_markups"),
		() => isSuperAdmin,
	];
	return checks.some((fn) => {
		try {
			return !!fn();
		} catch {
			return false;
		}
	});
};

export const filterPageRoutesByAccess = (routes, hasPermission, user, enabledAdminModuleSet, optionKeys) =>
	routes.filter((page) => {
	switch (page.href) {
			case ROUTES.ANALYTICS:
				return canAccessOrderAnalytics(hasPermission, user, enabledAdminModuleSet);
			case ROUTES.USER_ANALYTICS:
				return canAccessUserAnalytics(hasPermission, user, enabledAdminModuleSet);
			case ROUTES.SERVICES:
				return canAccessServices(hasPermission);
			case ROUTES.ADMIN:
				return canAccessAdmin(hasPermission, user);
			case ROUTES.ORDER_HISTORY:
				return canAccessOrderHistory(hasPermission);
			case ROUTES.SETTINGS:
				return canAccessSettings(user);
			case ROUTES.RSPL:
				return isRsplFeatureEnabled(optionKeys);
			default:
				return true;
		}
	});
